import React, { useState, useEffect } from "react"; // Import useRef
import { Dropdown, Spinner } from "react-bootstrap"; // Import Spinner
import PropTypes from 'prop-types'; // Import PropTypes
// Removed animate.css import
import "./StatusBadge.css";

const statusMappings = {
  draft: "draft",
  pending: "pending",
  validated: "validated",
  to_review: "to_review", // Renamed from Revision
  sent: "sent",
  won: "won",
  lost: "lost",
  archived: "archived", // Added archived status
};

// Add translateStatus to props
const StatusBadge = ({ status, displayStatus, onStatusChange, rowId, isUpdating, translateStatus }) => {
  // Ensure the status received from props is one of the valid keys, default to 'draft'
  const validStatus = status && statusMappings[status.toLowerCase()] ? status.toLowerCase() : 'draft';
  const [currentStatus, setCurrentStatus] = useState(validStatus);

  // Update local state when prop changes
  useEffect(() => {
    const newValidStatus = status && statusMappings[status.toLowerCase()] ? status.toLowerCase() : 'draft';
    // Only update if not currently updating, to avoid flickering
    if (!isUpdating) {
      setCurrentStatus(newValidStatus);
    }
  }, [status, isUpdating]);


  const handleStatusChange = (newStatus) => {
    // Prevent changing status while already updating
    if (isUpdating) return;

    // Only update if the status actually changed
    if (newStatus !== currentStatus) {
      // Optimistically update UI? Or wait for parent?
      // setCurrentStatus(newStatus); // Decide if optimistic update is desired
      if (onStatusChange) {
        onStatusChange(rowId, newStatus); // Pass the new lowercase status key
      }
    }
  };


  const badgeClassName = `status-badge status-${currentStatus}`;
  const contentClassName = `status-content d-flex align-items-center`;


  const statusTextToDisplay = displayStatus
    ? displayStatus
    : translateStatus // Use the translation function for the badge text if displayStatus is not provided
      ? translateStatus(currentStatus)
      : currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1).replace('_', ' ');

  return (
    <Dropdown>
      <Dropdown.Toggle
        variant="light"
        className={`${badgeClassName} d-flex align-items-center justify-content-between`}
        disabled={isUpdating} // Disable toggle when updating
      >
        {isUpdating ? (
          // Show spinner when updating
          <div className="status-content d-flex align-items-center"> {/* Keep original class here */}
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-1"
            />
            <span className="status-text">Mise à jour...</span>
          </div>
        ) : (
          // Show status content when not updating
          <>
            {/* Removed animation classes and key from this div */}
            <div className={contentClassName}>
              <span
                className="status-badge-dot"
                style={{ backgroundColor: getColor(currentStatus) }}
              ></span>
              {/* Display the determined status text */}
              <span className="status-text">{statusTextToDisplay}</span>
            </div>
            <span className="status-arrow">▼</span>
          </>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="status-dropdown-menu"
        popperConfig={{
          strategy: 'fixed', // Force fixed positioning
        }}
      >
        {Object.keys(statusMappings).map((statusKey) => {
          // Use the translateStatus function for dropdown item text
          const dropdownItemText = translateStatus(statusKey);
          return (
            <Dropdown.Item
              key={statusKey}
              onClick={() => handleStatusChange(statusKey)}
              className={`status-dropdown-item status-${statusKey}`}
            >
              <div className="status-content d-flex align-items-center">
                <span
                  className="status-badge-dot"
                  style={{ backgroundColor: getColor(statusKey) }}
                ></span>
                <span className="status-text" style={{ color: getColor(statusKey) }}>
                  {dropdownItemText} {/* Display translated text */}
                </span>
              </div>
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const getColor = (status) => {
  const colors = {
    draft: "#686b78",
    pending: "#8a7402",
    validated: "#0250a6",
    to_review: "#c4320a", // Mapped from Revision
    sent: "#5d18a5",
    won: "#26771b",
    lost: "#db2433",
    archived: "#686b78", // Using Draft color for Archived
  };
  return colors[status] || "#686B78"; // Default color
};

// Add prop types for validation
StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
  displayStatus: PropTypes.string,
  onStatusChange: PropTypes.func.isRequired,
  rowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isUpdating: PropTypes.bool,
  translateStatus: PropTypes.func.isRequired, // Add translateStatus prop type
};

// Default prop value for isUpdating and displayStatus
StatusBadge.defaultProps = {
  isUpdating: false,
  displayStatus: null,
  // Provide a default identity function for translateStatus
  translateStatus: (status) => status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' '),
};

export default StatusBadge;
